import { IonCol, IonInput, IonItem, IonLabel, IonRow } from '@ionic/react'
import { AutocompleteTypes } from '@ionic/core'
import React, { forwardRef, HTMLAttributes, RefAttributes, useEffect, useState } from 'react'
import { getStyleForPHI, IPHIComponent } from '../phi/phi.component'
import ValidationBadgeComponent from '../validationBadge/ValidationBadge.component'
import './StringInput.component.css'
import { IOptions, IPNNVOption } from '../../common/common.types'
import { useDispatch } from 'react-redux'
import {
  resetPNNVState,
  setFreeTextFieldCurrentPNNVValue,
  setFreeTextFieldOnPNNVChangeHandler,
  setFreeTextFieldPNNVIsOpen,
  setFreeTextFieldPNNVLabel,
  setFreeTextFieldPNNVListHeader,
  setFreeTextFieldPNNVOptions,
  setFreeTextFieldPNNVCustomEvent,
  setFreeTextFieldNonPNNVDataReset,
  setFreeTextPNNVNemsis,
} from '../pnnv/store/pnnv.actions'
import PNNVIcon from '../pnnv/PNNVIcon.component'
import { isPlatformWeb } from '../../utilities'
import { useAutofillGuardName } from '../../hooks'
export interface IStringInput
  extends HTMLAttributes<HTMLIonInputElement>,
    RefAttributes<HTMLIonInputElement>,
    IPHIComponent {
  dataTestID: string
  inputValue: string
  onChangeAction: (e: CustomEvent, dispatchData?: any) => void
  action?: string
  label?: string
  disabled?: boolean
  autocapitalize?: string
  maxlength?: number
  setIsFocused?: (isFocused: boolean) => void
  onFocusAction?: (e: React.FocusEvent<HTMLIonInputElement>) => void
  onBlurAction?: (e: React.FocusEvent<HTMLIonInputElement>) => void
  pnnvOptions?: IOptions
  pnnvValue?: IPNNVOption | null | undefined
  pnnvAction?: (...args: any[]) => any
  pnnvLabel?: string
  pnnvListHeader?: string
  dispatchData?: any
  nemsis?: string
  autocomplete?: AutocompleteTypes
}

const SInput: React.FC<IStringInput> = forwardRef<HTMLIonInputElement, IStringInput>((props: IStringInput, ref) => {
  const {
    dataTestID,
    inputValue,
    onChangeAction,
    action,
    label = '',
    disabled,
    isPHI,
    autoCapitalize = 'sentences',
    maxlength,
    onFocusAction = (e: React.FocusEvent<HTMLIonInputElement>) => () => void 0,
    onBlurAction = (e: React.FocusEvent<HTMLIonInputElement>) => () => void 0,
    setIsFocused = (isFocused: boolean) => () => void 0,
    pnnvOptions = [],
    pnnvValue = undefined,
    pnnvAction = () => void 0,
    pnnvLabel = '',
    pnnvListHeader = '',
    dispatchData,
    nemsis = 'nemsisCode',
    autocomplete = 'new-password',
    autocapitalize: _autocapitalize,
    ...rest
  } = props
  const [value, setValue] = useState(inputValue)
  const [isInFocus, setIsInFocus] = useState(inputValue ? true : false)
  const dispatch = useDispatch()
  const autofillGuardName = useAutofillGuardName(dataTestID)

  const setPNNVData = () => {
    dispatch(setFreeTextFieldPNNVOptions(pnnvOptions))
    dispatch(setFreeTextFieldCurrentPNNVValue(pnnvValue))
    dispatch(setFreeTextFieldOnPNNVChangeHandler(pnnvAction))
    dispatch(setFreeTextFieldPNNVLabel(pnnvLabel))
    dispatch(setFreeTextPNNVNemsis(nemsis))
    dispatch(setFreeTextFieldPNNVListHeader(pnnvListHeader || label))
  }

  useEffect(() => {
    setValue(inputValue)
  }, [inputValue])

  const shouldShowPNNVPWAControl = isPlatformWeb() && typeof pnnvAction !== 'undefined' && pnnvOptions.length > 0
  const floatLabel = isInFocus || value?.length || inputValue?.length || pnnvValue?.name?.length
  const onFocus = (e: React.FocusEvent<HTMLIonInputElement>) => {
    if (pnnvOptions.length) {
      dispatch(setFreeTextFieldPNNVCustomEvent(e))
      const newEventObject = new CustomEvent('ionInputFocusEvent', {
        ...e,
        detail: {
          value: '',
        },
      })
      if (onChangeAction)
        dispatch(setFreeTextFieldNonPNNVDataReset(() => onChangeAction(newEventObject as CustomEvent)))
      if (!isPlatformWeb()) {
        setPNNVData()
      }
      if (pnnvValue?.name) {
        dispatch(setFreeTextFieldPNNVIsOpen(true))
      }
    } else {
      dispatch(resetPNNVState())
    }
    setIsFocused(true)
    onFocusAction(e)
  }

  const onBlur = (e: React.FocusEvent<HTMLIonInputElement>) => {
    setIsFocused(false)
    onBlurAction(e)
  }

  const handleOnChange = (e: CustomEvent, calledFrom?: string) => {
    if (value === e.detail.value) return
    if (calledFrom === 'PNNV') {
      const newEventObjectWithEmptyValue = {
        ...e,
        detail: {
          ...e.detail,
          value: '',
        },
      }
      onChangeAction && onChangeAction(newEventObjectWithEmptyValue)
    } else {
      maxlength && e.detail.value?.length > maxlength
        ? setValue(e.detail.value.slice(0, maxlength)!)
        : setValue(e.detail.value!)
      onChangeAction && onChangeAction(e, dispatchData)
    }
  }

  const handlePNNVOptions = (
    e:
      | CustomEvent
      | React.MouseEvent<HTMLIonButtonElement>
      | React.MouseEvent<HTMLIonInputElement>
      | React.KeyboardEvent<HTMLIonInputElement>
  ) => {
    if (!isPlatformWeb()) return
    if (pnnvOptions.length) {
      const newEventObject = {
        ...e,
        detail: {
          value: '',
        },
      }
      dispatch(setFreeTextFieldNonPNNVDataReset(() => onChangeAction(newEventObject as any)))
      setPNNVData()
      if (pnnvValue?.name) {
        dispatch(setFreeTextFieldPNNVIsOpen(true))
      }
    } else {
      dispatch(resetPNNVState())
    }
    setIsFocused(false)
  }

  const renderLabel = () => {
    return (
      <IonLabel
        className={floatLabel ? 'string-input-ion-label-floating' : 'string-input-ion-label'}
        position='stacked'
        color='medium'>
        {label}
      </IonLabel>
    )
  }

  return (
    <IonItem className='string-input-ion-item' lines='none'>
      {action && <ValidationBadgeComponent action={action} />}
      {shouldShowPNNVPWAControl && (
        <IonRow className='string-input-ion-row gap-x-3'>
          {label && renderLabel()}
          {pnnvValue === null && (
            <IonCol className={`${floatLabel ? 'bottom-5px' : 'top-6px'} relative`}>
              <PNNVIcon
                tabIndex={-1}
                pnnvOptions={pnnvOptions}
                pnnvLabel={pnnvLabel}
                pnnvValue={pnnvValue}
                pnnvListHeader={pnnvListHeader || label}
                pnnvAction={pnnvAction}
                onButtonClick={handlePNNVOptions}
                className='pnnv-icon-pwa-string-input'
                dataTestId={dataTestID + '-pnnvIcon'}
                nemsis={nemsis}
              />
            </IonCol>
          )}
        </IonRow>
      )}
      {!shouldShowPNNVPWAControl && label && renderLabel()}
      <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
        {pnnvValue?.name ? (
          <IonInput
            disabled={disabled}
            data-testid={dataTestID}
            className='string-input'
            value={pnnvValue.name}
            color='primary'
            onIonChange={(e) => {
              handleOnChange(e, 'PNNV')
            }}
            onClick={handlePNNVOptions}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && isPlatformWeb()) {
                handlePNNVOptions(e)
              }
            }}
            onFocus={(e) => {
              onFocus(e)
              setIsInFocus(true)
            }}
            onBlur={(e) => {
              onBlur(e)
              setIsInFocus(false)
            }}
            {...rest}
            name={autofillGuardName}
            autocomplete={autocomplete}
          />
        ) : (
          <IonInput
            ref={ref}
            disabled={disabled}
            data-testid={dataTestID}
            className='string-input'
            value={value}
            color='primary-alt'
            onIonChange={(e) => {
              handleOnChange(e)
            }}
            onFocus={(e) => {
              onFocus(e)
              setIsInFocus(true)
            }}
            onBlur={(e) => {
              onBlur(e)
              setIsInFocus(false)
            }}
            autocapitalize={autoCapitalize}
            maxlength={maxlength}
            {...rest}
            name={autofillGuardName}
            autocomplete={autocomplete}
          />
        )}
      </div>
    </IonItem>
  )
})

SInput.displayName = 'SInput'

export default SInput
